import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { takePendingFile } from "@/lib/pending-upload";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeftRight, Download, Layers, RectangleHorizontal, RectangleVertical, Share2, Shuffle, Upload } from "lucide-react";

import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { ShareButtons } from "@/components/social/ShareButtons";
import { extractDominantColors } from "@/lib/aura-extract";
import { BACKGROUND_STYLES, ensurePaletteSize } from "@/lib/background-styles";
import { drawTextOverlay, TEXT_POSITION_PRESETS } from "@/lib/background-text";
import { cn } from "@/lib/utils";

type Status = "idle" | "loading" | "error" | "ready";

const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8MB

// Base dimensions are stored landscape-normalized (width >= height, except
// Square which is symmetric); the Orientation control below flips w/h so
// each ratio covers both a horizontal and vertical use — e.g. Widescreen
// 16:9 flipped vertically is the "Story" 9:16 format, so there's no need
// for a separate, redundant preset.
const RATIOS = [
  { id: "square", label: "Square · 1:1", w: 1080, h: 1080 },
  { id: "classic", label: "Classic · 3:2", w: 1620, h: 1080 },
  { id: "widescreen", label: "Widescreen · 16:9", w: 1920, h: 1080 },
  { id: "frame", label: "Frame · 5:4", w: 1350, h: 1080 },
  { id: "custom", label: "Custom Size", w: 1080, h: 1080 },
] as const;

type RatioId = (typeof RATIOS)[number]["id"];
type Orientation = "horizontal" | "vertical";

const MIN_CUSTOM_DIM = 200;
const MAX_CUSTOM_DIM = 3000;

function clampDim(v: number): number {
  if (!Number.isFinite(v)) return MIN_CUSTOM_DIM;
  return Math.round(Math.min(MAX_CUSTOM_DIM, Math.max(MIN_CUSTOM_DIM, v)));
}

const FONTS = [
  { id: "serif", label: "Fraunces (Serif)", family: "'Fraunces', Georgia, serif" },
  { id: "sans", label: "Inter (Sans)", family: "'Inter', 'Helvetica Neue', sans-serif" },
  { id: "playfair", label: "Playfair Display", family: "'Playfair Display', Georgia, serif" },
  { id: "poppins", label: "Poppins", family: "'Poppins', 'Helvetica Neue', sans-serif" },
  { id: "condensed", label: "Oswald (Condensed)", family: "'Oswald', 'Arial Narrow', sans-serif" },
  { id: "classic", label: "Classic Serif", family: "Georgia, 'Times New Roman', serif" },
  { id: "mono", label: "Typewriter", family: "'Courier New', monospace" },
  { id: "display", label: "Bold Display", family: "Impact, 'Arial Black', sans-serif" },
  { id: "ultraBold", label: "Anton (Ultra Bold)", family: "'Anton', 'Arial Black', sans-serif" },
  { id: "handwritten", label: "Caveat (Handwritten)", family: "'Caveat', cursive" },
  { id: "script", label: "Pacifico (Script)", family: "'Pacifico', cursive" },
] as const;

type FontId = (typeof FONTS)[number]["id"];

/** Text swatches always start with a safe near-white and near-black for
 *  contrast, then follow with every color actually extracted from the
 *  uploaded photo — so captions can match the image's own palette instead
 *  of a fixed, unrelated color set. */
function buildTextColorOptions(palette: string[]): string[] {
  const base = ["#FFFFFF", "#0B0B0F"];
  const extra = palette.filter((c) => !base.some((b) => b.toLowerCase() === c.toLowerCase()));
  return [...base, ...extra];
}

const THUMB_SIZE = 156;

const HOW_IT_WORKS =
  "Background Maker reads your photo directly in the browser, samples its pixels into a compact histogram, and picks out the handful of colors that dominate the image. Those colors — not the photo itself — become the ingredients for 19 different procedural patterns spanning glowing silk bursts, mesh and grainy gradients, fused liquid metaballs, holographic foil, aurora ribbons, neon gradient remaps, halftone dot grids, frosted glass panels, flat Memphis-pop shapes, a retro synthwave horizon, faceted low-poly, sweeping gradients, blurred glows, organic blobs, watercolor washes, paint splatter, a palette-tinted cloudscape, and a scatter of game-controller symbols. Because every pattern is generated with plain canvas drawing instead of filters applied to the photo, you get an entirely new, abstract image that still carries the mood of the original — the same warm sunset photo might become a glowing silk burst or a sky full of orange-tinted clouds, depending on the style you pick. Pick any preset size, flip it horizontal or vertical, or dial in an exact custom width and height. Nothing is ever uploaded to a server to make this happen; the extraction, the pattern generation, the text overlay, and the final PNG export all happen on your device, so the photo you use to build a background never leaves your browser tab.";

export default function BackgroundMaker() {
  useSeo({
    title: "Background Maker — Turn Any Photo Into a Colorful Background | Mychromamind",
    description:
      "Upload a photo and generate colorful, ready-to-share backgrounds in 19 styles — silk flow, mesh gradient, grainy gradient, liquid metaball, holographic chrome, aurora glow, neon gradient, halftone, glassmorphism, memphis pop, synthwave grid, low-poly, gradient flow, frosted glow, organic blobs, watercolor, paint splatter, cloudscape, and game symbols — with custom aspect ratios, orientation, exact sizing, and text overlays. Runs entirely in your browser.",
    path: "/background-maker",
    ogType: "website",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Background Maker",
          applicationCategory: "DesignApplication",
          operatingSystem: "Any (Web)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Upload a photo to extract its color palette and generate colorful wallpapers and social backgrounds in multiple styles, with adjustable aspect ratio and text overlays.",
          url: "https://mychromamind.com/background-maker",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Mychromamind", item: "https://mychromamind.com/" },
            { "@type": "ListItem", position: 2, name: "Background Maker", item: "https://mychromamind.com/background-maker" },
          ],
        },
      ],
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const thumbRefs = useRef<Record<string, HTMLCanvasElement | null>>({});
  const photoImgRef = useRef<HTMLImageElement | null>(null);

  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [palette, setPalette] = useState<string[]>([]);
  const [seed, setSeed] = useState(0);
  const [styleId, setStyleId] = useState<string>(BACKGROUND_STYLES[0].id);
  const [ratioId, setRatioId] = useState<RatioId>("square");
  const [orientation, setOrientation] = useState<Orientation>("horizontal");
  const [customW, setCustomW] = useState(1080);
  const [customH, setCustomH] = useState(1080);
  const [fontsReady, setFontsReady] = useState(false);


  const [photoLayerEnabled, setPhotoLayerEnabled] = useState(false);
  const [photoLayerOpacity, setPhotoLayerOpacity] = useState(40);

  const [text, setText] = useState("");
  const [fontId, setFontId] = useState<FontId>("serif");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [textPos, setTextPos] = useState({ x: 0.5, y: 0.5 });
  const [textScale, setTextScale] = useState<"sm" | "md" | "lg">("md");
  const previewWrapRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const textColorOptions = useMemo(() => buildTextColorOptions(palette), [palette]);

  const ratio = useMemo(() => {
    if (ratioId === "custom") {
      const w = clampDim(customW);
      const h = clampDim(customH);
      return { id: "custom" as const, label: "Custom Size", w, h };
    }
    const base = RATIOS.find((r) => r.id === ratioId) ?? RATIOS[0];
    if (base.id === "square" || orientation === "horizontal") return base;
    return { ...base, w: base.h, h: base.w };
  }, [ratioId, orientation, customW, customH]);
  const font = useMemo(() => FONTS.find((f) => f.id === fontId) ?? FONTS[0], [fontId]);
  const selectedStyle = useMemo(
    () => BACKGROUND_STYLES.find((s) => s.id === styleId) ?? BACKGROUND_STYLES[0],
    [styleId],
  );

  // Canvas text needs the custom webfont to actually be loaded before the
  // first draw, or the browser silently falls back to a system font.
  useEffect(() => {
    document.fonts?.ready?.then(() => setFontsReady(true)).catch(() => setFontsReady(true));
  }, []);

  const handleFile = useCallback((file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (JPG, PNG, WEBP).");
      setStatus("error");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError("That image is too large — please choose one under 8MB.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const dominant = extractDominantColors(img, 6).map((c) => c.hex);
        const finalPalette = ensurePaletteSize(dominant, 4);
        photoImgRef.current = img;
        setPalette(finalPalette);
        setSeed(Date.now() & 0xffffffff);
        setStyleId(BACKGROUND_STYLES[0].id);
        setStatus("ready");
      } catch {
        setError("Something went wrong while reading that photo. Try a different one.");
        setStatus("error");
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      setError("That file couldn't be loaded as an image. Try a JPG or PNG.");
      setStatus("error");
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, []);

  // Auto-load a file passed from the homepage via the pending-upload store.
  useEffect(() => {
    const pending = takePendingFile();
    if (pending) handleFile(pending);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setStatus("idle");
    setPalette([]);
    setText("");
    setTextColor("#FFFFFF");
    setTextPos({ x: 0.5, y: 0.5 });
    photoImgRef.current = null;
    setPhotoLayerEnabled(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Lets people drag their caption anywhere on the preview instead of only
  // picking from fixed slots — pointer coordinates are converted to a 0..1
  // position relative to the preview box, which maps 1:1 onto the canvas.
  const updateTextPosFromPointer = useCallback((clientX: number, clientY: number) => {
    const rect = previewWrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const x = Math.min(0.97, Math.max(0.03, (clientX - rect.left) / rect.width));
    const y = Math.min(0.97, Math.max(0.03, (clientY - rect.top) / rect.height));
    setTextPos({ x, y });
  }, []);

  const handlePreviewPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!text.trim()) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateTextPosFromPointer(e.clientX, e.clientY);
  };
  const handlePreviewPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateTextPosFromPointer(e.clientX, e.clientY);
  };
  const handlePreviewPointerUp = () => {
    draggingRef.current = false;
  };

  const handleShuffle = () => setSeed((s) => (s + 104729) & 0xffffffff);

  // Render every style's small square swatch in the gallery.
  useEffect(() => {
    if (status !== "ready") return;
    BACKGROUND_STYLES.forEach((style) => {
      const canvas = thumbRefs.current[style.id];
      if (!canvas) return;
      canvas.width = THUMB_SIZE;
      canvas.height = THUMB_SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      style.draw(ctx, THUMB_SIZE, THUMB_SIZE, palette, seed);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, palette, seed, BACKGROUND_STYLES]);

  // Render the full-resolution main preview/export canvas whenever any
  // adjustment changes.
  useEffect(() => {
    if (status !== "ready" || !fontsReady) return;
    const canvas = mainCanvasRef.current;
    if (!canvas) return;
    canvas.width = ratio.w;
    canvas.height = ratio.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Photo base layer — drawn first so the generated style sits on top.
    if (photoLayerEnabled && photoImgRef.current) {
      const img = photoImgRef.current;
      const scale = Math.max(ratio.w / img.naturalWidth, ratio.h / img.naturalHeight);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      const dx = (ratio.w - dw) / 2;
      const dy = (ratio.h - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
      // Style renders on top; lower globalAlpha lets the photo bleed through.
      ctx.globalAlpha = 1 - photoLayerOpacity / 100;
    }

    selectedStyle.draw(ctx, ratio.w, ratio.h, palette, seed);
    ctx.globalAlpha = 1;

    drawTextOverlay(ctx, ratio.w, ratio.h, {
      text,
      fontFamily: font.family,
      color: textColor,
      x: textPos.x,
      y: textPos.y,
      scale: textScale,
    });
  }, [status, fontsReady, selectedStyle, ratio, palette, seed, text, font, textColor, textPos, textScale, photoLayerEnabled, photoLayerOpacity]);

  const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

  const handleDownload = () => {
    const canvas = mainCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "mychromamind-background.png";
      link.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function";

  const handleShareImage = () => {
    const canvas = mainCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        const file = new File([blob], "mychromamind-background.png", { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: "My Background", text: "Made with Mychromamind's Background Maker" });
        }
      } catch {
        // User cancelled the native share sheet, or the platform declined — no action needed.
      }
    }, "image/png");
  };

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 md:py-16 w-full">
        <h1 className="text-3xl sm:text-5xl font-serif mb-4 leading-tight text-center">
          Background Maker
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg text-center max-w-2xl mx-auto mb-10">
          Upload any photo and instantly turn its color palette into a colorful, ready-to-share
          background — 19 styles from silk flow and mesh gradients to metaballs, holographic
          chrome, aurora glow, neon gradient, halftone, glassmorphism, memphis pop, synthwave grid,
          cloudscape, and game symbols — in any orientation or an exact custom size, with an
          optional caption. Everything renders in your browser; your photo is never uploaded
          anywhere.
        </p>

        {status !== "ready" && (
          <div className="max-w-lg mx-auto">
            <label
              htmlFor="bg-maker-file-input"
              className="group flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border hover:border-primary/50 bg-card/60 hover:bg-card transition-colors cursor-pointer px-6 py-14 text-center"
            >
              <span className="text-4xl" aria-hidden="true">🎨</span>
              <span className="font-medium text-foreground">
                {status === "loading" ? "Reading your photo's colors…" : "Click to upload a photo"}
              </span>
              <span className="text-xs text-muted-foreground">JPG, PNG, or WEBP — up to 8MB</span>
              <input
                id="bg-maker-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={status === "loading"}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>

            {status === "error" && (
              <div role="alert" className="mt-4 p-4 rounded-xl border border-destructive/30 bg-destructive/5 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        {status === "ready" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Extracted palette + reset */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2">
                {palette.map((c) => (
                  <span key={c} className="w-8 h-8 rounded-full ring-2 ring-background shadow-md" style={{ backgroundColor: c }} title={c} />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:bg-muted transition-colors font-medium text-sm"
                >
                  <Upload className="w-4 h-4" />
                  New Photo
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10">
              {/* Controls */}
              <div className="space-y-8">
                {/* Style gallery */}
                <div>
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Choose a Style
                  </h2>
                  <div className="grid grid-cols-3 gap-3">
                    {BACKGROUND_STYLES.map((style) => (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => setStyleId(style.id)}
                        className={cn(
                          "group rounded-xl overflow-hidden border-2 transition-colors text-left",
                          styleId === style.id ? "border-primary" : "border-border hover:border-primary/40",
                        )}
                      >
                        <canvas
                          ref={(el) => { thumbRefs.current[style.id] = el; }}
                          data-testid={`background-maker-style-thumb-${style.id}`}
                          className="w-full aspect-square block"
                        />
                        <span className="block px-2 py-1.5 text-xs font-medium text-foreground">{style.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photo layer */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" />
                      Photo Layer
                    </h2>
                    <button
                      type="button"
                      onClick={() => setPhotoLayerEnabled((v) => !v)}
                      aria-pressed={photoLayerEnabled}
                      className={cn(
                        "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        photoLayerEnabled ? "bg-primary" : "bg-muted-foreground/30"
                      )}
                    >
                      <span
                        className={cn(
                          "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform",
                          photoLayerEnabled ? "translate-x-5" : "translate-x-0"
                        )}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Composite your photo behind the style so the original image shows through.
                  </p>
                  {photoLayerEnabled && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label htmlFor="photo-layer-opacity" className="text-xs font-medium text-muted-foreground">
                          Photo visibility
                        </label>
                        <span className="text-xs font-mono text-foreground tabular-nums">{photoLayerOpacity}%</span>
                      </div>
                      <input
                        id="photo-layer-opacity"
                        type="range"
                        min={5}
                        max={90}
                        step={5}
                        value={photoLayerOpacity}
                        onChange={(e) => setPhotoLayerOpacity(Number(e.target.value))}
                        className="w-full accent-primary"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground/60 mt-0.5">
                        <span>Subtle</span>
                        <span>Strong</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Aspect ratio */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Size
                    </h2>
                    {ratioId !== "square" && ratioId !== "custom" && (
                      <button
                        type="button"
                        onClick={() => setOrientation((o) => (o === "horizontal" ? "vertical" : "horizontal"))}
                        data-testid="background-maker-orientation-toggle"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border hover:border-primary/40 hover:bg-muted transition-colors text-xs font-medium text-muted-foreground"
                        aria-label="Toggle horizontal or vertical orientation"
                      >
                        {orientation === "horizontal" ? (
                          <RectangleHorizontal className="w-3.5 h-3.5" />
                        ) : (
                          <RectangleVertical className="w-3.5 h-3.5" />
                        )}
                        {orientation === "horizontal" ? "Horizontal" : "Vertical"}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {RATIOS.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRatioId(r.id)}
                        data-testid={`background-maker-ratio-${r.id}`}
                        className={cn(
                          "px-3 py-2 rounded-lg border text-xs font-medium transition-colors",
                          ratioId === r.id
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-primary/40",
                        )}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>

                  {ratioId === "custom" && (
                    <div className="flex items-end gap-3">
                      <div>
                        <label htmlFor="bg-custom-w" className="block text-xs font-medium text-muted-foreground mb-1.5">
                          Width (px)
                        </label>
                        <input
                          id="bg-custom-w"
                          type="number"
                          min={MIN_CUSTOM_DIM}
                          max={MAX_CUSTOM_DIM}
                          value={customW}
                          onChange={(e) => setCustomW(Number(e.target.value))}
                          onBlur={(e) => setCustomW(clampDim(Number(e.target.value)))}
                          className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setCustomW(customH);
                          setCustomH(customW);
                        }}
                        aria-label="Swap width and height"
                        className="mb-0.5 p-2 rounded-lg border border-border hover:border-primary/40 hover:bg-muted transition-colors text-muted-foreground"
                      >
                        <ArrowLeftRight className="w-4 h-4" />
                      </button>
                      <div>
                        <label htmlFor="bg-custom-h" className="block text-xs font-medium text-muted-foreground mb-1.5">
                          Height (px)
                        </label>
                        <input
                          id="bg-custom-h"
                          type="number"
                          min={MIN_CUSTOM_DIM}
                          max={MAX_CUSTOM_DIM}
                          value={customH}
                          onChange={(e) => setCustomH(Number(e.target.value))}
                          onBlur={(e) => setCustomH(clampDim(Number(e.target.value)))}
                          className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Text overlay */}
                <div>
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Add Text (optional)
                  </h2>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Write a caption, quote, or name…"
                    rows={2}
                    maxLength={140}
                    className="w-full rounded-xl border border-border bg-card p-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none mb-4"
                  />

                  <div className="mb-4">
                    <label htmlFor="bg-font" className="block text-xs font-medium text-muted-foreground mb-1.5">Font</label>
                    <select
                      id="bg-font"
                      value={fontId}
                      onChange={(e) => setFontId(e.target.value as FontId)}
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
                    >
                      {FONTS.map((f) => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">
                      Position — pick a spot, or drag the text directly on the preview
                    </p>
                    <div className="grid grid-cols-3 gap-1.5 w-32">
                      {TEXT_POSITION_PRESETS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          aria-label={p.label}
                          title={p.label}
                          onClick={() => setTextPos({ x: p.x, y: p.y })}
                          className={cn(
                            "aspect-square rounded-lg border flex items-center justify-center transition-colors",
                            Math.abs(textPos.x - p.x) < 0.01 && Math.abs(textPos.y - p.y) < 0.01
                              ? "border-primary bg-primary/10"
                              : "border-border hover:border-primary/40",
                          )}
                        >
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              Math.abs(textPos.x - p.x) < 0.01 && Math.abs(textPos.y - p.y) < 0.01
                                ? "bg-primary"
                                : "bg-muted-foreground/50",
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">
                        Text Color — from your photo
                      </p>
                      <div className="flex items-center gap-2 flex-wrap max-w-[220px]">
                        {textColorOptions.map((c) => (
                          <button
                            key={c}
                            type="button"
                            aria-label={`Use text color ${c}`}
                            onClick={() => setTextColor(c)}
                            className={cn(
                              "w-7 h-7 rounded-full ring-2 ring-offset-2 ring-offset-background transition-shadow",
                              textColor.toLowerCase() === c.toLowerCase() ? "ring-primary" : "ring-transparent",
                            )}
                            style={{ backgroundColor: c, border: c === "#FFFFFF" ? "1px solid var(--border)" : undefined }}
                          />
                        ))}
                        <input
                          type="color"
                          aria-label="Custom text color"
                          value={textColor}
                          onChange={(e) => setTextColor(e.target.value)}
                          className="w-7 h-7 rounded-full border border-border cursor-pointer bg-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Size</p>
                      <div className="flex items-center gap-1.5">
                        {(["sm", "md", "lg"] as const).map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setTextScale(s)}
                            className={cn(
                              "w-8 h-8 rounded-lg border text-xs font-medium uppercase transition-colors",
                              textScale === s
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-muted-foreground hover:border-primary/40",
                            )}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live preview + actions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Preview
                  </h2>
                  <button
                    type="button"
                    onClick={handleShuffle}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors font-medium text-xs"
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                    Shuffle Pattern
                  </button>
                </div>
                <div
                  ref={previewWrapRef}
                  onPointerDown={handlePreviewPointerDown}
                  onPointerMove={handlePreviewPointerMove}
                  onPointerUp={handlePreviewPointerUp}
                  onPointerCancel={handlePreviewPointerUp}
                  className={cn(
                    "w-full rounded-2xl overflow-hidden border border-border shadow-lg bg-muted mb-2 touch-none select-none",
                    text.trim() ? "cursor-move" : "",
                  )}
                  style={{ aspectRatio: `${ratio.w} / ${ratio.h}` }}
                >
                  <canvas
                    ref={mainCanvasRef}
                    id="background-maker-preview-canvas"
                    data-testid="background-maker-preview-canvas"
                    className="w-full h-full block"
                  />
                </div>
                {text.trim() && (
                  <p className="text-xs text-muted-foreground mb-4">Tip: click and drag anywhere on the preview to move your text.</p>
                )}

                <div className={cn("flex flex-col sm:flex-row gap-3 mb-2", !text.trim() && "mt-4")}>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download PNG
                  </button>
                  {canShareFiles && (
                    <button
                      type="button"
                      onClick={handleShareImage}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-border hover:bg-muted transition-colors font-medium text-sm"
                    >
                      <Share2 className="w-4 h-4" />
                      Share Image
                    </button>
                  )}
                </div>

                <ShareButtons
                  label="Share the Background Maker"
                  text="I made a colorful custom background with Mychromamind's Background Maker — try it on your own photo:"
                  className="mb-2"
                />
              </div>
            </div>

            <div className="max-w-2xl mx-auto mt-14 mb-10">
              <h2 className="text-xl font-serif mb-3 text-center">How It Works</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{HOW_IT_WORKS}</p>
            </div>

            <div className="max-w-2xl mx-auto mb-10">
              <h2 className="text-xl font-serif mb-4 text-center">Style Guide</h2>
              <dl className="space-y-4">
                {BACKGROUND_STYLES.map((style) => (
                  <div key={style.id}>
                    <dt className="font-medium text-sm text-foreground mb-0.5">{style.name}</dt>
                    <dd className="text-sm text-muted-foreground leading-relaxed">{style.description}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <AdSlot
              slotId="2431849490"
              content={[HOW_IT_WORKS, ...BACKGROUND_STYLES.map((s) => s.description)]}
              className="my-10 max-w-2xl mx-auto"
            />

            <div className="max-w-2xl mx-auto text-center mb-6">
              <Link href="/aura-scanner" className="text-sm text-primary font-medium hover:underline">
                Curious what your photo's colors say about you? Try the Aura Scanner →
              </Link>
            </div>

            <p className="text-center text-xs text-muted-foreground max-w-md mx-auto">
              Backgrounds are generated entirely in your browser from your photo's color palette —
              the photo itself is never uploaded to a server.
            </p>
          </motion.div>
        )}
      </div>
    </Shell>
  );
}
